import { useMemo, type ReactNode } from 'react';
import {
	Cell,
	Legend,
	Line,
	LineChart,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
} from 'recharts';

import type { StatTile, TrendDirection } from '@/api/types';
import { useOverviewData } from '@/api/overview/hooks';
import type { OverviewData } from '@/api/overview/mock';
import { VolumeAndHealthPanel } from '@/pages/overview/_components/volume-and-health';
import { PageContainer } from '@/components/layout/page-container';
import { PageHeader } from '@/components/layout/page-header';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useInView } from '@/lib/use-in-view';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Shared chart chrome
// ---------------------------------------------------------------------------

const AXIS_TICK = { fill: 'var(--fg-subtle)', fontSize: 12 };

const TOOLTIP_STYLE = {
	backgroundColor: 'var(--popover)',
	border: '1px solid var(--border-strong)',
	borderRadius: 8,
	color: 'var(--fg)',
	fontSize: 12,
} as const;

const DONUT_COLORS: Record<string, string> = {
	Delivered: 'var(--success)',
	Failed: 'var(--danger)',
	Bounced: 'var(--warning)',
	Pending: 'var(--brand-navy)',
};

// ---------------------------------------------------------------------------
// Presentational primitives (overview-local)
// ---------------------------------------------------------------------------

function PanelCard({
	title,
	children,
	className,
}: {
	title?: string;
	children: ReactNode;
	className?: string;
}) {
	return (
		<Card
			className={cn('rounded-lg border-border p-5 shadow-none', className)}
		>
			{title ? (
				<h2 className="mb-4 font-serif text-base font-semibold text-foreground">
					{title}
				</h2>
			) : null}
			{children}
		</Card>
	);
}

const TREND_CLASS: Record<TrendDirection, string> = {
	up: 'text-success',
	down: 'text-danger',
	flat: 'text-muted-foreground',
};

const TREND_GLYPH: Record<TrendDirection, string> = {
	up: '↑',
	down: '↓',
	flat: '→',
};

/** Compact stat rows shown inside the Delivery Status card. */
function DeliveryStatusStats({ tiles }: { tiles: StatTile[] }) {
	return (
		<ul className="mt-5 flex flex-col gap-2 border-t border-border pt-4">
			{tiles.map((tile) => {
				const trend = tile.trend ?? 'flat';
				return (
					<li
						key={tile.id}
						className="flex items-baseline justify-between gap-3"
					>
						<span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
							{tile.label}
						</span>
						<span className="flex items-baseline gap-2">
							<span className="font-serif text-lg font-bold text-foreground">
								{tile.value}
							</span>
							{typeof tile.deltaPct === 'number' ? (
								<span className={`text-xs font-medium ${TREND_CLASS[trend]}`}>
									{TREND_GLYPH[trend]} {tile.deltaPct > 0 ? '+' : ''}
									{tile.deltaPct}%
								</span>
							) : null}
						</span>
					</li>
				);
			})}
		</ul>
	);
}

// ---------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------

const PROVIDER_LINE_COLORS: Record<string, string> = {
	SES: 'var(--brand-navy)',
	SendGrid: 'var(--success)',
	Postmark: 'var(--warning)',
	Mailgun: 'var(--danger)',
};

function ProviderVolumeLineChart({
	data,
}: {
	data: OverviewData['volumeByProviderOverTime'];
}) {
	const fmtDate = (iso: string) => iso.slice(5); // MM-DD
	const { ref, inView } = useInView<HTMLDivElement>();
	// Pivot ProviderVolumeSeries[] (one array per provider) into a flat,
	// date-keyed row array — the shape Recharts LineChart needs, one dataKey
	// per provider series sharing a single x-axis.
	const rows = useMemo(() => {
		const byDate = new Map<string, Record<string, number | string>>();
		for (const series of data) {
			for (const point of series.points) {
				const row = byDate.get(point.date) ?? { date: point.date };
				row[series.provider] = point.value;
				byDate.set(point.date, row);
			}
		}
		return Array.from(byDate.values()).sort((a, b) =>
			String(a.date).localeCompare(String(b.date)),
		);
	}, [data]);
	return (
		<div ref={ref}>
			<ResponsiveContainer width="100%" height={260}>
				<LineChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
					<XAxis
						dataKey="date"
						tickFormatter={fmtDate}
						tick={AXIS_TICK}
						axisLine={false}
						tickLine={false}
					/>
					<Tooltip
						contentStyle={TOOLTIP_STYLE}
						labelFormatter={(l: string) => fmtDate(l)}
						cursor={{ stroke: 'var(--border-strong)' }}
					/>
					<Legend
						iconType="circle"
						verticalAlign="top"
						align="left"
						wrapperStyle={{ fontSize: 12, color: 'var(--fg-muted)', paddingBottom: 12 }}
					/>
					{data.map((series) => (
						<Line
							key={series.provider}
							type="monotone"
							dataKey={series.provider}
							name={series.provider}
							stroke={PROVIDER_LINE_COLORS[series.provider] ?? 'var(--brand-gold)'}
							strokeWidth={2}
							dot={false}
							activeDot={{ r: 4 }}
							isAnimationActive={inView}
						/>
					))}
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
}

function StatusDonutChart({ data }: { data: OverviewData['statusSplit'] }) {
	const total = data.reduce((sum, slice) => sum + slice.value, 0) || 1;
	const { ref, inView } = useInView<HTMLDivElement>();
	return (
		<div className="flex items-center gap-8">
			<div ref={ref} className="h-[200px] w-[200px] shrink-0">
				<ResponsiveContainer width="100%" height="100%">
					<PieChart>
						<Pie
							data={data}
							dataKey="value"
							nameKey="name"
							innerRadius={64}
							outerRadius={92}
							paddingAngle={2}
							cornerRadius={4}
							stroke="none"
							isAnimationActive={inView}
						>
							{data.map((slice) => (
								<Cell
									key={slice.name}
									fill={DONUT_COLORS[slice.name] ?? 'var(--brand-gold)'}
								/>
							))}
						</Pie>
						<Tooltip contentStyle={TOOLTIP_STYLE} />
					</PieChart>
				</ResponsiveContainer>
			</div>
			<ul className="flex flex-col gap-3.5">
				{data.map((slice) => (
					<li key={slice.name} className="flex items-start gap-2.5">
						<span
							className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-[3px]"
							style={{ background: DONUT_COLORS[slice.name] ?? 'var(--brand-gold)' }}
						/>
						<span>
							<span className="block text-[15px] font-semibold leading-tight text-foreground">
								{Math.round((slice.value / total) * 100)}%
							</span>
							<span className="block text-xs text-muted-foreground">{slice.name}</span>
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}

// ---------------------------------------------------------------------------
// State scaffolding
// ---------------------------------------------------------------------------

function CardSkeleton({ className = '' }: { className?: string }) {
	return (
		<Skeleton
			className={cn(
				'h-full min-h-24 rounded-lg border border-border bg-card',
				className,
			)}
		/>
	);
}

function OverviewSkeleton() {
	return (
		<PageContainer>
			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				{Array.from({ length: 4 }).map((_, i) => (
					<CardSkeleton key={i} className="min-h-80" />
				))}
			</div>
		</PageContainer>
	);
}

function OverviewError({
	message,
	onRetry,
}: {
	message: string;
	onRetry: () => void;
}) {
	return (
		<PageContainer>
			<div className="flex flex-col items-center gap-4 rounded-lg border border-border bg-card py-16">
				<p className="text-sm text-muted-foreground">
					Could not load the dashboard: {message}
				</p>
				<button
					type="button"
					onClick={onRetry}
					className="border border-border-strong bg-secondary px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
				>
					Try again
				</button>
			</div>
		</PageContainer>
	);
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function OverviewPage() {
	const { data, isLoading, isError, error, refetch } = useOverviewData();

	if (isLoading) return <OverviewSkeleton />;
	if (isError || !data) {
		return (
			<OverviewError
				message={error instanceof Error ? error.message : 'unknown error'}
				onRetry={() => void refetch()}
			/>
		);
	}

	return (
		<PageContainer>
			<PageHeader
				title="Overview"
				description="Email delivery health across all providers."
			/>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<PanelCard title="Volume by Provider">
					<ProviderVolumeLineChart data={data.volumeByProviderOverTime} />
				</PanelCard>
				<PanelCard title="Delivery Status">
					<StatusDonutChart data={data.statusSplit} />
					<DeliveryStatusStats tiles={data.statTiles} />
				</PanelCard>
				<VolumeAndHealthPanel data={data.volumeAndHealth} />
			</div>
		</PageContainer>
	);
}
